import React, { useState, useCallback, useRef } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import { MoreVertical, Folder, FolderOpen, File, Plus, Edit, Trash2, Copy } from 'lucide-react';

const ItemType = 'TREE_NODE';

const TreeNode = ({ node, level, onToggle, onRightClick, onDrop, selectedNode, onSelectNode, expandedNodes }) => {
  const [isExpanded, setIsExpanded] = useState(expandedNodes.has(node.id));
  const hasChildren = node.children && node.children.length > 0;
  
  const [{ isDragging }, drag] = useDrag({
    type: ItemType,
    item: { id: node.id, name: node.name },
    collect: (monitor) => ({
      isDragging: monitor.isDragging(),
    }),
  });

  const [{ isOver }, drop] = useDrop({
    accept: ItemType,
    drop: (item) => {
      if (item.id !== node.id) {
        onDrop(item.id, node.id); // Drop come child
      }
    },
    canDrop: (item) => item.id !== node.id,
    collect: (monitor) => ({
      isOver: monitor.isOver(),
    }),
  });

  // Drop zone per inserire come fratello
  const [{ isOverAfter }, dropAfter] = useDrop({
    accept: ItemType,
    drop: (item) => {
      if (item.id !== node.id) {
        // Trova il parent del nodo corrente
        const parentId = node.parent || null;
        onDrop(item.id, parentId); // Sposta come fratello (stesso parent)
      }
    },
    canDrop: (item) => item.id !== node.id,
    collect: (monitor) => ({
      isOverAfter: monitor.isOver(),
    }),
  });

  const handleToggle = useCallback(() => {
    const newExpanded = !isExpanded;
    setIsExpanded(newExpanded);
    onToggle(node.id, newExpanded);
  }, [isExpanded, onToggle, node.id]);

  const handleRightClick = useCallback((e) => {
    e.preventDefault();
    onRightClick(e, node);
  }, [node, onRightClick]);

  const handleClick = useCallback(() => {
    onSelectNode(node);
  }, [node, onSelectNode]);

  const nodeStyle = {
    paddingLeft: `${level * 20}px`,
    opacity: isDragging ? 0.5 : 1,
    backgroundColor: isOver ? '#e0f2fe' : selectedNode?.id === node.id ? '#f0f9ff' : 'transparent',
  };

  return (
    <div>
      <div
        ref={(node) => drag(drop(node))}
        style={nodeStyle}
        className={`flex items-center py-2 px-3 cursor-pointer hover:bg-gray-50 border-b border-gray-100 transition-colors ${
          selectedNode?.id === node.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
        }`}
        onClick={handleClick}
        onContextMenu={handleRightClick}
      >
        <div className="flex items-center flex-1">
          {hasChildren && (
            <button
              data-folder-id={node.id}
              onClick={handleToggle}
              className="mr-2 p-1 hover:bg-gray-200 rounded transition-colors"
            >
              {isExpanded ? (
                <FolderOpen className="w-4 h-4 text-blue-600" />
              ) : (
                <Folder className="w-4 h-4 text-gray-600" />
              )}
            </button>
          )}
          
          {!hasChildren && (
            <div className="mr-2">
              <File className="w-4 h-4 text-gray-400" />
            </div>
          )}
          
          {node.immagine ? (
            <img
              src={node.immagine}
              alt={node.name}
              className="w-6 h-6 mr-2 rounded-full object-cover"
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.nextSibling.style.display = 'block';
              }}
            />
          ) : null}
          
          {!node.immagine && (
            <div className="w-6 h-6 mr-2 rounded-full bg-gray-200 flex items-center justify-center">
              <span className="text-xs text-gray-500">
                {node.name.charAt(0).toUpperCase()}
              </span>
            </div>
          )}
          
          <span 
            className={`text-sm ${
              !node.hasTransactions 
                ? 'font-bold text-gray-900'  // Bold per non-transazionali (raggruppamento)
                : 'font-normal text-gray-700' // Normal per transazionali
            } cursor-pointer hover:text-blue-600 transition-colors`}
            onDoubleClick={() => {
              // Emula il click sul folder solo se ha figli
              if (node.children && node.children.length > 0) {
                const folderElement = document.querySelector(`[data-folder-id="${node.id}"]`);
                if (folderElement) {
                  folderElement.click();
                }
              }
            }}
          >
            {node.name}
          </span>
          
          {node.hasTransactions && (
            <span className="ml-2 px-2 py-1 text-xs rounded-full bg-orange-100 text-orange-800">
              Transazionale
            </span>
          )}
          
          {/* Saldo allineato a destra */}
          <span className={`ml-auto text-sm font-mono ${
            node.saldo >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            €{node.saldo?.toFixed(2) || '0.00'}
          </span>
          
          <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
            node.stato === 'attivo' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {node.stato}
          </span>
        </div>
        
        <button
          onClick={(e) => {
            e.stopPropagation();
            onRightClick(e, node);
          }}
          className="p-1 hover:bg-gray-200 rounded transition-colors opacity-0 hover:opacity-100"
        >
          <MoreVertical className="w-4 h-4 text-gray-600" />
        </button>
      </div>
      
      {/* Drop zone visiva per inserire come fratello */}
      <div 
        ref={dropAfter}
        className={`h-2 mx-4 rounded transition-all duration-200 ${
          isOverAfter ? 'bg-blue-400' : 'bg-transparent'
        }`}
      />
      
      {hasChildren && isExpanded && (
        <div>
          {node.children.map((child) => (
            <TreeNode
              key={child.id}
              node={child}
              level={level + 1}
              onToggle={onToggle}
              onRightClick={onRightClick}
              onDrop={onDrop}
              selectedNode={selectedNode}
              onSelectNode={onSelectNode}
              expandedNodes={expandedNodes}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const ContextMenu = ({ x, y, node, onClose, onAction }) => {
  const menuRef = useRef(null);

  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const menuItems = [
    ...(node.hasTransactions ? [] : [{ icon: Plus, label: 'Aggiungi Sottoconto', action: 'add' }]),
    { icon: Edit, label: 'Modifica', action: 'edit' },
    { icon: Copy, label: 'Duplica', action: 'duplicate' },
    { icon: Trash2, label: 'Elimina', action: 'delete' },
  ];

  return (
    <div
      ref={menuRef}
      className="fixed bg-white rounded-lg shadow-lg border border-gray-200 py-2 z-50 min-w-[180px]"
      style={{ left: x, top: y }}
    >
      {menuItems.map(({ icon: Icon, label, action }) => (
        <button
          key={action}
          onClick={() => {
            onAction(action, node);
            onClose();
          }}
          className="w-full px-4 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-3 transition-colors"
        >
          <Icon className="w-4 h-4 text-gray-600" />
          <span>{label}</span>
        </button>
      ))}
    </div>
  );
};

const TreeView = ({ data, onNodeAction, onNodeDrop }) => {
  const [contextMenu, setContextMenu] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [expandedNodes, setExpandedNodes] = useState(new Set([1])); // Espandi Home di default

  const handleRightClick = useCallback((e, node) => {
    setContextMenu({ x: e.clientX, y: e.clientY, node });
  }, []);

  const handleContextMenuClose = useCallback(() => {
    setContextMenu(null);
  }, []);

  const handleMenuAction = useCallback((action, node) => {
    if (onNodeAction) {
      onNodeAction(action, node);
    }
  }, [onNodeAction]);

  const handleToggle = useCallback((nodeId, isExpanded) => {
    setExpandedNodes((prev) => {
      const newSet = new Set(prev);
      if (isExpanded) {
        newSet.add(nodeId);
      } else {
        newSet.delete(nodeId);
      }
      return newSet;
    });
  }, []);

  const handleDrop = useCallback((draggedNodeId, targetNodeId) => {
    if (onNodeDrop) {
      onNodeDrop(draggedNodeId, targetNodeId);
    }
  }, [onNodeDrop]);

  const buildTree = (flatData) => {
    const nodeMap = {};
    const roots = [];

    // Crea mappa dei nodi
    flatData.forEach((node) => {
      nodeMap[node.id] = { ...node, children: [] };
    });

    // Costruisci gerarchia
    flatData.forEach((node) => {
      if (node.parent === null || node.parent === undefined) {
        roots.push(nodeMap[node.id]);
      } else if (nodeMap[node.parent]) {
        nodeMap[node.parent].children.push(nodeMap[node.id]);
      }
    });

    // Funzione ricorsiva per ordinare per posizione poi nome
    const sortByPosition = (nodes) => {
      // Ordina i nodi correnti: posizione prima, poi nome
      nodes.sort((a, b) => {
        const posA = a.posizione;
        const posB = b.posizione;
        
        // Se entrambi hanno posizione definita e diversa
        if (posA !== null && posB !== null && posA !== posB) {
          return posA - posB;
        }
        
        // Se solo uno ha posizione definita, quello con posizione viene prima
        if (posA !== null && posB === null) return -1;
        if (posA === null && posB !== null) return 1;
        
        // A parità di posizione (entrambi null o entrambi uguali), ordina per nome
        return a.name.localeCompare(b.name);
      });
      
      // Ordina ricorsivamente i figli
      nodes.forEach(node => {
        if (node.children && node.children.length > 0) {
          sortByPosition(node.children);
        }
      });
    };

    // Ordina le radici e tutti i figli
    sortByPosition(roots);

    return roots;
  };

  const treeData = buildTree(data);

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm">
      <div className="p-4 border-b border-gray-200">
        <h2 className="text-lg font-semibold text-gray-900">GLEDGER - Contabilità</h2>
        <p className="text-sm text-gray-600 mt-1">Gestione conti gerarchica</p>
      </div>
      
      <div className="max-h-[600px] overflow-y-auto">
        {treeData.map((node) => (
          <TreeNode
            key={node.id}
            node={node}
            level={0}
            onToggle={handleToggle}
            onRightClick={handleRightClick}
            onDrop={handleDrop}
            selectedNode={selectedNode}
            onSelectNode={setSelectedNode}
            expandedNodes={expandedNodes}
          />
        ))}
      </div>

      {contextMenu && (
        <ContextMenu
          x={contextMenu.x}
          y={contextMenu.y}
          node={contextMenu.node}
          onClose={handleContextMenuClose}
          onAction={handleMenuAction}
        />
      )}
    </div>
  );
};

export default TreeView;
